import { pluralize } from "@/lib/utils";

import { ToolLine } from "../aui/ToolLine";
import { useThreadId } from "../chat/useThreadId";
import type { NativeCard, NativeCardProps } from ".";
import { SentFlowCard } from "./SentFlowCard";
import { useOnFake } from "./useOnFake";

/** The fields of a `list_merchant_tasks` row the card reads; the rest it re-reads live (S6). */
type ListedTask = { merchant_task_id: number; application_id: number; status: string };

function isListedTask(row: unknown): row is ListedTask {
  const task = row as Partial<ListedTask> | null;
  return typeof task?.merchant_task_id === "number" && typeof task.application_id === "number";
}

function listedTasks(result: unknown): ListedTask[] {
  return Array.isArray(result) ? result.filter(isListedTask) : [];
}

/** The tasks that draw as cards: the open ones. Rows stored before `application_id` have none. */
export function openTasks(result: unknown): ListedTask[] {
  return listedTasks(result).filter((task) => task.status === "open");
}

function MerchantTasks({ result, artifact }: NativeCardProps) {
  // The list is a Steddy read, so it runs on the fake too; its tasks' Resend and Invalidate do not.
  const onFake = useOnFake(null, useThreadId());
  const open = openTasks(result);
  // The line reads the call, not its rows: say why fewer cards show than tasks were read.
  const closed = listedTasks(result).length - open.length;
  return (
    <div className="flex flex-col gap-2">
      <ToolLine
        line={artifact.line}
        state={artifact.state}
        detail={closed > 0 ? `${pluralize(closed, "closed task")} not shown` : undefined}
      />
      {open.map((task) => (
        <SentFlowCard
          key={task.merchant_task_id}
          borrowerId={task.application_id}
          flowId={task.merchant_task_id}
          outbound={!onFake}
        />
      ))}
    </div>
  );
}

/** `list_merchant_tasks`: each open task as a live sent-flow card; with none open, just the line. */
export const MERCHANT_TASKS_CARD: NativeCard = {
  Card: MerchantTasks,
  shows: (artifact, result) => artifact.state === "success" && openTasks(result).length > 0,
};
